import { useState, useEffect } from "react";
import { capitalize } from "lodash";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  NativeSelect,
  Sheet,
  SheetContent,
  Typography,
  toast,
} from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getMaskData, setMaskData } from "@/helpers/apis";
import { useNavigate } from "react-router-dom";
import EditSquareIcon from "../../assets/icons/edit_square_security.svg";
import { useAppState } from "@/modules/app/useAppContext";
import { PermissionsEnum } from "../../helpers/constants";
import { MaskingType } from "./types";
import { isFreeStack } from "@/helpers/tenant";
import MasksView from "./MasksView";
import { useIntercom } from "react-use-intercom";
import type { MaskDataItem, MaskDataResponse, MaskTypeMap } from "./types";
import { RelativeComponentLoader } from "@/components/loader";
import { default as LoadingSpinner } from "@/assets/icons/loading-spinner.svg?react";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { SecurityTracking } from "@/lib/tracking/security";

const GET_MASK_DATA = "GET-MASK-DATA";

const Security = () => {
  useBreadcrumb("Data Protection", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });
  const { show } = useIntercom();
  const { loggedinUser: user } = useAppState();
  const navigate = useNavigate();
  const [maskType, setMaskType] = useState<MaskTypeMap>({});
  const [updatingMasks, setUpdatingMasks] = useState<Record<string, boolean>>({});
  const { data: maskData, isPending: isLoading } = useQuery({
    queryKey: [GET_MASK_DATA],
    queryFn: () => getMaskData(),
  });

  // Handle successful mask data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (maskData) {
      let d: MaskTypeMap = {};
      maskData.mask_data.forEach(item => {
        d[item.label] = item.masking_type;
      });
      setMaskType(d);
    }
  }, [maskData]);
  const [temp, setTemp] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [label, setLabel] = useState("");
  const [defaultVal, setDefaultVal] = useState("");
  const [customMaskType, setCustomMaskType] = useState<MaskingType>(MaskingType.ALLOW);
  const [regex, setRegex] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState<string | null>(null);

  const { mutate: setMask } = useMutation({
    mutationFn: (data: MaskDataResponse) => setMaskData(data),
    onSuccess: () => {
      toast({ description: "Mask type updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to update mask type: ${error.message || error}`,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setUpdatingMasks({});
    },
  });

  const { mutate: setMaskWithRefresh, isPending: isSaving } = useMutation({
    mutationFn: (data: MaskDataResponse) => setMaskData(data),
    onSuccess: () => {
      toast({ description: "Mask saved successfully" });
      navigate(0);
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to save mask: ${error.message || error}`,
        variant: "destructive",
      });
    },
  });

  const handleMaskTypeChange = (e: string, i: string) => {
    let d = { ...maskType };
    d[i] = e;
    setMaskType(d);
    setUpdatingMasks({ ...updatingMasks, [i]: true });
    SecurityTracking.maskTypeChanged(i, e);
    if (maskData) {
      const updatedMaskData = {
        ...maskData,
        mask_data: maskData.mask_data.map(item => ({
          ...item,
          masking_type: d[item.label] as MaskingType,
        })),
      };
      setMask(updatedMaskData);
    }
    // To rerender
    setTemp(!temp);
  };

  const toggleSidebar = () => {
    if (showSidebar) {
      setEditMode(false);
      setEditTitle(null);
      setLabel("");
      setRegex("");
      setCustomMaskType(MaskingType.ALLOW);
      setDefaultVal("");
    }
    setShowSidebar(prev => !prev);
  };

  const handleLabel = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  };

  const handleCustomMaskTypeChange = (e: string) => {
    setCustomMaskType(e as MaskingType);
  };
  const handleRegex = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegex(e.target.value);
  };
  const handleDefaultVal = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDefaultVal(e.target.value);
  };

  const handleSave = () => {
    let customMaskTypeData: MaskDataItem = {
      label: label,
      masking_type: customMaskType,
    };
    if (regex) {
      customMaskTypeData["regex"] = regex;
    }
    if (defaultVal) {
      customMaskTypeData["default"] = defaultVal;
    }
    if (maskData) {
      let updatedMaskData;
      if (editMode) {
        SecurityTracking.maskEdited(label, customMaskType);
        updatedMaskData = {
          ...maskData,
          mask_data: maskData.mask_data.map(d => {
            if (d.label !== editTitle) {
              return d;
            } else {
              return customMaskTypeData;
            }
          }),
        };
      } else {
        SecurityTracking.maskCreated(customMaskType);
        updatedMaskData = {
          ...maskData,
          mask_data: [...maskData.mask_data, customMaskTypeData],
        };
      }
      setMaskWithRefresh(updatedMaskData);
    }
  };

  const handleDelete = () => {
    if (maskData) {
      SecurityTracking.maskDeleted(label);
      const updatedMaskData = {
        ...maskData,
        mask_data: maskData.mask_data.filter(item => item.label !== label),
      };
      setMaskWithRefresh(updatedMaskData);
    }
  };

  const handleAddButtonClick = () => {
    setEditMode(false);
    toggleSidebar();
  };

  const handleEditButtonClick = (item: MaskDataItem) => {
    setEditMode(true);
    setEditTitle(item.label);
    setLabel(item.label);
    setRegex(item.regex || "");
    setCustomMaskType(item.masking_type);
    setDefaultVal(item.default || "");
    toggleSidebar();
  };

  if (!user?.permission?.includes(PermissionsEnum.SECURITY_VIEW)) {
    return <div>Not Enough Permissions to access this page</div>;
  }

  if (isFreeStack()) {
    return (
      <Card>
        <CardHeader>
          <Typography variant="page-heading">Data Protection</Typography>
        </CardHeader>
        <CardContent className="al-p-4">
          <div className="al-mb-4 al-bg-bg-brand al-p-4 al-rounded-2xl">
            <Typography variant="body">
              This is an enterprise only feature. Please{" "}
              <Button variant="link" onClick={() => show()} className="!al-p-0">
                contact support
              </Button>{" "}
              to enable this feature.
            </Typography>
          </div>
          <MasksView />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="al-p-6 al-max-w-[700px]">
      <div className="al-bg-white al-rounded-lg al-shadow-sm al-p-6">
        <Typography variant="page-heading">Data Protection</Typography>
        {isLoading ? (
          <RelativeComponentLoader label={"Loading security settings..."} />
        ) : (
          <>
            {maskData?.mask_data.map((item, i) => {
              return (
                <Stack
                  key={i}
                  direction="row"
                  className="al-items-center al-justify-between al-py-4 al-border-b al-border-gray-200 last:al-border-0"
                >
                  <button
                    className="al-text-gray-700 hover:al-text-gray-900 al-bg-transparent al-border-none al-cursor-pointer al-p-0"
                    onClick={() => handleEditButtonClick(item)}
                  >
                    {item.label}:
                  </button>
                  <Stack direction="row" className="al-items-center al-gap-2">
                    {updatingMasks[item.label] && (
                      <LoadingSpinner className="al-w-4 al-h-4 al-animate-spin" />
                    )}
                    <div
                      className={
                        updatingMasks[item.label] ? "al-opacity-60 al-pointer-events-none" : ""
                      }
                    >
                      <NativeSelect
                        variant={"outline"}
                        options={Object.values(MaskingType).map(v => ({
                          label: capitalize(v),
                          value: v,
                        }))}
                        buttonProps={{ "aria-label": `Update ${item.label} mask type` }}
                        id={`mask-dropdown-${i}`}
                        onChange={(e: string) => {
                          handleMaskTypeChange(e, item.label);
                        }}
                        value={maskType[item.label]}
                      />
                    </div>

                    <button
                      className="al-p-2 al-bg-transparent al-border-none al-cursor-pointer hover:al-bg-gray-100 al-rounded"
                      onClick={() => handleEditButtonClick(item)}
                    >
                      <img className="al-w-5 al-h-5" src={EditSquareIcon} alt="edit_square" />
                    </button>
                  </Stack>
                </Stack>
              );
            })}
          </>
        )}
      </div>
      <Button className="al-mt-4" onClick={handleAddButtonClick}>
        Add
      </Button>
      <Sheet open={showSidebar} onOpenChange={toggleSidebar}>
        <SheetContent aria-label="Security entity modal">
          <div className="al-p-6">
            {editMode ? (
              <Typography variant="h4" className="al-mb-6">
                {editTitle}
              </Typography>
            ) : (
              <Typography variant="h4" className="al-mb-6">
                Add New Entity
              </Typography>
            )}
            <Stack direction="column" className="al-gap-4">
              <Stack direction="row" className="al-items-center al-justify-between">
                <Typography className="al-text-gray-700">Entity:</Typography>
                <Input
                  className="al-w-[240px]"
                  placeholder="Birthdate"
                  onChange={handleLabel}
                  value={label}
                />
              </Stack>
              <Stack direction="row" className="al-items-center al-justify-between">
                <Typography className="al-text-gray-700">Regex:</Typography>
                <Input
                  className="al-w-[240px]"
                  placeholder="^(0[1-9]|1[0-2])/(0[1-9]|1\d|2\d|3[01])/(\d{4})$"
                  onChange={handleRegex}
                  value={regex}
                />
              </Stack>
              <Stack direction="row" className="al-items-center al-justify-between">
                <Typography className="al-text-gray-700">Action:</Typography>
                <NativeSelect
                  variant={"outline"}
                  options={["allow", "block", "mask"].map(v => ({
                    label: capitalize(v),
                    value: v,
                  }))}
                  id="mask-dropdown-custom"
                  onChange={(e: string) => {
                    handleCustomMaskTypeChange(e);
                  }}
                  value={customMaskType}
                />
              </Stack>
              {customMaskType === "mask" && (
                <Stack direction="row" className="al-items-center al-justify-between">
                  <Typography className="al-text-gray-700">Replace With:</Typography>
                  <Input
                    className="al-w-[240px]"
                    placeholder="01/01/1997"
                    onChange={handleDefaultVal}
                    value={defaultVal}
                  />
                </Stack>
              )}
            </Stack>
            <Stack direction="row" className="al-justify-between al-items-center al-mt-6">
              {editMode ? (
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                </Button>
              ) : (
                <div></div>
              )}
              <Stack direction="row" className="al-gap-3">
                <Button variant="outline" onClick={toggleSidebar}>
                  Cancel
                </Button>
                <LoadingButton onClick={handleSave} disabled={!label} loading={isSaving}>
                  Save
                </LoadingButton>
              </Stack>
            </Stack>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Security;
